import { secondaryFontFamily } from '@openeducation/oe-rn-themes';
import React, { ReactNode } from 'react';
import { StyleSheet } from 'react-native';
import { Button as PaperButton, useTheme } from 'react-native-paper';

import { TestId } from '../../constants/test-id';
import useTypeModeColors, {
  UseTypeModeColorsProps,
} from '../../hooks/use-type-mode-colors';
import {
  fs,
  lineHeight,
  mh,
  ml,
  mv,
  color as colorStyleUtil,
  borderColor as borderColorStyleUtil,
} from '../../style-utils';

export type ButtonType = 'accent' | 'primary' | 'danger' | 'dark0';
type ButtonSize = 'small' | 'regular' | 'large' | 'huge';

const DEFAULT_ICON_MARGIN = 12;

const buttonSizing = {
  small: 12,
  regular: 14,
  large: 16,
  huge: 20,
} as const;

export type OEButtonProps = {
  size?: ButtonSize;
  IconRight?: ReactNode;
} & UseTypeModeColorsProps &
  React.ComponentProps<typeof PaperButton>;

export const OEButton: React.FC<OEButtonProps> = ({
  children,
  uppercase = false,
  mode = 'contained',
  type = 'primary',
  size = 'regular',
  ghost = false,
  color,
  style,
  labelStyle,
  contentStyle,
  disabled,
  icon,
  testID = TestId.OE_BUTTON,
  IconRight,
  ...props
}: OEButtonProps) => {
  const { colors } = useTheme();
  const { labelColor, borderColor, backgroundColor } = useTypeModeColors({
    mode,
    type,
    ghost,
    color,
    disabled,
  });

  return (
    <PaperButton
      {...props}
      testID={testID}
      disabled={disabled}
      uppercase={uppercase}
      mode={mode}
      icon={icon}
      color={backgroundColor}
      labelStyle={[
        colorStyleUtil(labelColor),
        styles.label,
        mv(8),
        mh(buttonSizing[size]),
        fs(buttonSizing[size]),
        lineHeight(buttonSizing[size] + 6),
        labelStyle,
      ]}
      contentStyle={[
        !!icon && ml(buttonSizing[size] - DEFAULT_ICON_MARGIN),
        contentStyle,
      ]}
      style={[
        styles.button,
        ghost && [styles.ghostButton, borderColorStyleUtil(colors.white)],
        borderColorStyleUtil(borderColor),
        style,
      ]}
    >
      {children}
      {IconRight}
    </PaperButton>
  );
};

const styles = StyleSheet.create({
  button: {
    borderRadius: 100,
    shadowOpacity: 0,
    elevation: 0,
  },
  label: {
    ...secondaryFontFamily.medium,
  },
  ghostButton: {
    borderWidth: 1,
  },
});
